/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import {
  REGISTER_SUCCESS,
  REGISTER_FAIL,
  REGISTER_CONFIRMATION_SUCCESS,
  REGISTER_CONFIRMATION_FAIL,
  LOGIN_SUCCESS,
  LOGIN_FAIL,
  LOGOUT,
  UPDATE_TOURS_SUCCESS,
  UPDATE_TOURS_FAIL,
  UPDATE_COGNITO_SETTINGS_SUCCESS,
  UPDATE_COGNITO_SETTINGS_FAIL,
  SET_GLOBAL_TOUR_LOCK,
  CLEAR_GLOBAL_TOUR_LOCK,
  SET_MOBILE_APP_MODE,
  PAYMENT_ERROR,
  UPDATE_CONSENT,
} from '../actions/types';

const emptyConsent = {
  essential: null,
  analytics: null,
  externalMedia: null,
};

function getConsent() {
  const consent = localStorage.getItem('consent');
  if (consent) {
    return JSON.parse(consent);
  }
  return emptyConsent;
}

function createInitialState() {
  return {
    isLoggedIn: null,
    authInProgress: false,
    authStatus: null, // 'inProgress' | 'success' | 'failed'
    mobileAppMode: false,
    profile: {
      attributes: null,
      settings: null,
    },
    tourLock: null,
    payment: {
      status: 'idle',
    },
    consent: getConsent(),
    rehydrationStatus: 'notStarted', // other values: 'inProgress', 'finished'
  };
}

const initialState = createInitialState();

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setAuthProgress(state) {
      state.authInProgress = true;
      state.authStatus = 'inProgress';
    },
    setAuthFailed(state) {
      state.authInProgress = false;
      state.authStatus = 'failed';
    },
    setAuthSuccess(state) {
      state.authInProgress = false;
      state.authStatus = 'success';
    },
    setAuthInProgress(state, action) {
      const nextValue = action.payload ?? true;
      state.authInProgress = Boolean(nextValue);
      state.authStatus = state.authInProgress ? 'inProgress' : 'idle';
    },
    setRehydrationStatus(state, action) {
      state.rehydrationStatus = action.payload;
    },
    setSettingsCurrent(state, action) {
      const nextCurrent = Number(action.payload);
      if (!Number.isFinite(nextCurrent)) return;

      if (!state.profile) state.profile = { attributes: null, settings: {} };
      if (!state.profile.settings) state.profile.settings = {};

      state.profile.settings.current = nextCurrent;
    },
    hydrate(state, action) {
      return {
        ...state,
        ...action.payload,
      };
    },
    clear(state) {
      const consent = state.consent ? { ...state.consent } : getConsent();
      return {
        ...createInitialState(),
        isLoggedIn: false,
        consent,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(REGISTER_SUCCESS, (state, action) => {
        state.isLoggedIn = false;
        state.registration = action.payload;
        state.authInProgress = false;
        state.authStatus = 'success';
      })
      .addCase(REGISTER_FAIL, (state) => {
        state.isLoggedIn = false;
        state.authInProgress = false;
        state.authStatus = 'failed';
      })
      .addCase(REGISTER_CONFIRMATION_SUCCESS, (state, action) => {
        state.registration = {
          ...(state.registration || {}),
          status: action.payload?.status,
        };
        state.authInProgress = false;
        state.authStatus = 'success';
      })
      .addCase(REGISTER_CONFIRMATION_FAIL, (state) => {
        state.registration = {
          ...(state.registration || {}),
          status: 'error',
        };
        state.authInProgress = false;
        state.authStatus = 'failed';
      })
      .addCase(LOGIN_SUCCESS, (state, action) => {
        const { payload } = action;
        state.isLoggedIn = true;
        state.profile.attributes = { toursSeen: payload?.toursSeen ?? null };
        state.profile.settings = {
          ...(state.profile.settings || {}),
          ...(payload?.settings || {}),
        };
        state.registration = {};
        state.sub = payload?.sub;
        state.authInProgress = false;
        state.authStatus = 'success';
      })
      .addCase(LOGIN_FAIL, (state) => {
        state.isLoggedIn = false;
        state.profile = {
          attributes: null,
          settings: null,
        };
        state.authInProgress = false;
        state.authStatus = 'failed';
      })
      .addCase(LOGOUT, (state) => {
        const consent = state.consent ? { ...state.consent } : getConsent();
        const resetState = createInitialState();
        resetState.isLoggedIn = false;
        resetState.consent = consent;
        resetState.authInProgress = false;
        resetState.authStatus = 'idle';
        return resetState;
      })
      .addCase(SET_MOBILE_APP_MODE, (state) => {
        state.mobileAppMode = true;
      })
      .addCase(UPDATE_COGNITO_SETTINGS_SUCCESS, (state, action) => {
        state.profile.settings = {
          ...(state.profile.settings || {}),
          ...(action.payload?.settings || {}),
        };
        state.profile.attributes = {
          ...(state.profile.attributes || {}),
          ...(action.payload?.attributes || {}),
        };
      })
      .addCase(UPDATE_COGNITO_SETTINGS_FAIL, () => {})
      .addCase(UPDATE_TOURS_SUCCESS, (state, action) => {
        state.profile.attributes = {
          ...(state.profile.attributes || {}),
          toursSeen: action.payload,
        };
      })
      .addCase(UPDATE_TOURS_FAIL, () => {})
      .addCase(SET_GLOBAL_TOUR_LOCK, (state) => {
        state.tourLock = true;
      })
      .addCase(CLEAR_GLOBAL_TOUR_LOCK, (state) => {
        state.tourLock = null;
      })
      .addCase(PAYMENT_ERROR, (state) => {
        state.payment.status = 'error';
      })
      .addCase(UPDATE_CONSENT, (state, action) => {
        state.consent = {
          ...(action.payload || {}),
        };
      })
      .addCase('viewer/getSharedReport/fulfilled', (state, action) => {
        if (action.payload?.user?.profile) {
          state.profile = action.payload.user.profile;
        }
      })
      .addCase('SET_REHYDRATION_STATUS', (state, action) => {
        state.rehydrationStatus = action.payload;
      });
  },
});

export const {
  setAuthFailed,
  setAuthProgress,
  setAuthSuccess,
  setAuthInProgress,
  setRehydrationStatus,
  setSettingsCurrent,
  hydrate,
  clear,
} = userSlice.actions;

export default userSlice.reducer;
